import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import { queryClient } from "../lib/query-client";
import { useToast } from "./useToast";
import type { ApiResponse } from "../types/auth";
import type {
  FeedbackItem,
  FeedbackListResponse,
  FeatureRating,
  FeatureRatingsResponse,
} from "../types/feedback";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const feedbackKeys = {
  all: ["feedback"] as const,
  list: (params?: FeedbackQueryParams) => ["feedback", "list", params] as const,
  detail: (id: string) => ["feedback", "detail", id] as const,
  ratings: () => ["feedback", "ratings"] as const,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FeedbackQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  type?: string;
  sort?: string;
}

export interface SubmitFeedbackPayload {
  title: string;
  description: string;
  type: string;
  priority: string;
  url?: string;
  page_url?: string;
  browser_info?: string;
  files?: File[];
}

export interface SubmitRatingPayload {
  feature_key: string;
  rating: number;
  comment?: string;
}

export interface AddCommentPayload {
  text: string;
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useFeedbackList(params: FeedbackQueryParams = {}) {
  return useQuery({
    queryKey: feedbackKeys.list(params),
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams();
      if (params.page != null) qs.set("page", String(params.page));
      if (params.limit != null) qs.set("limit", String(params.limit));
      if (params.status) qs.set("status", params.status);
      if (params.type) qs.set("type", params.type);
      if (params.sort) qs.set("sort", params.sort);

      const queryString = qs.toString();
      const url = queryString ? `/feedback?${queryString}` : "/feedback";

      const res = await apiClient.get<ApiResponse<FeedbackListResponse>>(url, {
        signal,
      });
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export function useFeedbackDetail(id: string) {
  return useQuery({
    queryKey: feedbackKeys.detail(id),
    queryFn: async ({ signal }) => {
      const res = await apiClient.get<ApiResponse<FeedbackItem>>(
        `/feedback/${id}`,
        { signal }
      );
      return res.data;
    },
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export function useFeatureRatings() {
  return useQuery({
    queryKey: feedbackKeys.ratings(),
    queryFn: async ({ signal }) => {
      const res = await apiClient.get<ApiResponse<FeatureRatingsResponse>>(
        "/feedback/ratings",
        { signal }
      );
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useSubmitFeedback(onSuccess?: () => void) {
  const toast = useToast();

  return useMutation({
    mutationFn: async (payload: SubmitFeedbackPayload) => {
      const formData = new FormData();
      formData.append("title", payload.title);
      formData.append("description", payload.description);
      formData.append("type", payload.type);
      formData.append("priority", payload.priority);
      if (payload.url) formData.append("url", payload.url);
      if (payload.page_url) formData.append("page_url", payload.page_url);
      if (payload.browser_info)
        formData.append("browser_info", payload.browser_info);
      if (payload.files) {
        for (const file of payload.files) {
          formData.append("files", file);
        }
      }
      // Do NOT set Content-Type — browser sets the multipart boundary
      const res = await apiClient.post<ApiResponse<FeedbackItem>>(
        "/feedback",
        formData
      );
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
      toast.success("Feedback submitted! Thank you.");
      onSuccess?.();
    },
    onError: () => {
      toast.error("Failed to submit feedback. Please try again.");
    },
  });
}

export function useSubmitRating() {
  const toast = useToast();

  return useMutation({
    mutationFn: async (payload: SubmitRatingPayload) => {
      const res = await apiClient.post<ApiResponse<FeatureRating>>(
        "/feedback/ratings",
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.ratings() });
    },
    onError: () => {
      toast.error("Failed to save rating.");
    },
  });
}

export function useAddComment(feedbackId: string) {
  const toast = useToast();

  return useMutation({
    mutationFn: async (payload: AddCommentPayload) => {
      const res = await apiClient.post<ApiResponse<FeedbackItem>>(
        `/feedback/${feedbackId}/comments`,
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: feedbackKeys.all,
      });
    },
    onError: () => {
      toast.error("Failed to post reply. Please try again.");
    },
  });
}
